import { useWorkspacesStore, Workspace } from "@shared/states/workspacesStore";
import PubNub from "pubnub";
import React from "react";
import { useIntl } from "react-intl";
import { useNavigate } from "react-router-dom";

import { WORK_ORDER_BROADCAST_TOPICS } from "~/constants/workOrders";
import { clearRejectedChannels } from "~/hooks/_pubnub-unread-messages";
import {
  useGetChatCredentials,
  useGetCurrentUser,
  useUserLogin,
} from "~/services";
import { base64String } from "~/utils";
import { broadcaster } from "~/utils/_appBroadcast";

const LOGGED_IN_USER_KEY = "logged_in_user";

const isFunction = (functionToCheck) =>
  functionToCheck && {}.toString.call(functionToCheck) === "[object Function]";

/**
 * The one PubNub client for the signed-in user, shared by every `useAuth`
 * caller.
 *
 * Logging out does not reload the page (see `useLogout`), so this outlives the
 * session that created it. A PubNub client is bound to one uuid for life and a
 * PAM token only authorizes the uuid it was minted for — so when the next user
 * signs in, reusing the old client and only swapping the token sends the new
 * user's token under the previous user's uuid, and every request is a 403
 * until a reload builds a fresh client. The client is therefore replaced
 * whenever the uuid changes.
 *
 * Held outside React so all callers see the same instance, with subscribers
 * so a replacement re-renders each of them: everything that subscribes or
 * attaches listeners keys its effect on the instance, and has to move over.
 */
let chatRef: PubNub | null = null;
const chatSubscribers = new Set<() => void>();

const subscribeToChat = (onChange: () => void) => {
  chatSubscribers.add(onChange);
  return () => {
    chatSubscribers.delete(onChange);
  };
};

const getChat = () => chatRef;

const replaceChat = (next: PubNub | null) => {
  const previous = chatRef;
  chatRef = next;
  // Drops the previous user's subscriptions and stops its long-poll, which
  // would otherwise keep retrying their channels with a token that isn't
  // theirs.
  previous?.destroy();
  chatSubscribers.forEach((onChange) => onChange());
};

const useAuth = () => {
  const navigate = useNavigate();
  const { messages: msg } = useIntl();
  const setWorkspaces = useWorkspacesStore((s) => s.setWorkspaces);
  const chat = React.useSyncExternalStore(subscribeToChat, getChat);

  const { currentUser, loading } = useGetCurrentUser();
  const { credentials: chatCredentials } = useGetChatCredentials({
    skip: !currentUser,
  });

  const { submitLogin } = useUserLogin();

  React.useEffect(() => {
    const uuid = chatCredentials?.uuid;
    if (chatCredentials?.keys && uuid && chatRef?.getUUID() !== uuid) {
      replaceChat(
        new PubNub({
          ...(chatCredentials.keys as Record<string, string>),
          uuid,
          suppressLeaveEvents: true,
        } as ConstructorParameters<typeof PubNub>[0]),
      );
      // Refusals were recorded against the previous user's token.
      clearRejectedChannels();
    }
    const token = chatCredentials?.token;
    const validToken = chatRef?._tokenManager?._token === token;

    if (!validToken && token) {
      // A REPLACEMENT token, not the first one. The first is simply the session
      // starting; a replacement means the grants changed underneath us.
      const isReplacement = !!chatRef?._tokenManager?._token;
      chatRef?.setToken?.(token);

      /**
       * New grants, so anything PubNub refused under the old token deserves
       * another try — and the unread map has to be rebuilt from channels that
       * can now actually be read. The client is one long-lived PubNub instance,
       * so swapping the token changes no object identity and re-runs no effect:
       * without saying so here, nothing would ever notice.
       */
      if (isReplacement) {
        clearRejectedChannels();
        // No channels named — the subscriber reads that as "re-read everything",
        // which is the only honest answer when a whole grant changed.
        broadcaster.publish(WORK_ORDER_BROADCAST_TOPICS.MEMBERSHIP_UPDATED, []);
      }
    }
  }, [chatCredentials?.keys, chatCredentials?.uuid, chatCredentials?.token]);

  //setting up workspaces in global state
  React.useEffect(() => {
    if (currentUser) {
      const workspaces: Workspace[] = [];
      if (currentUser?.oem) {
        workspaces.push({
          type: "OEM",
          id: currentUser.oem._id,
          oem_id: currentUser.oem._id,
          name: currentUser.oem.name,
          icon: currentUser.oem.brandLogo,
        });
      }
      if (currentUser.portals && currentUser.portals.length > 0) {
        currentUser.portals.forEach((portal) => {
          workspaces.push({
            type: "PORTAL",
            id: portal.linkedConnection,
            oem_id: portal._id,
            name: portal.name,
            portalName: portal.portalName,
            icon: portal.brandLogo,
            parentConnectionIds: (portal.portalHierarchy ?? []).map(String),
            allowSubPortalCreation: !!portal.allowSubPortalCreation,
          });
        });
      }
      setWorkspaces(workspaces);
    }
  }, [currentUser, setWorkspaces]);

  if (loading) return <p>{msg.common?.loading}</p>;

  const login = ({ email, username, password, organizationId }, callback) => {
    submitLogin({
      userCredentials: {
        username: email?.toLowerCase() || username?.toLowerCase(),
        password: base64String(password),
        organization: organizationId,
      },
    })
      .then((res) => {
        if (!!callback && isFunction(callback)) {
          callback(null, res);
        } else navigate("/");
        const loggedInUser = localStorage.getItem(LOGGED_IN_USER_KEY);
        const keysToRemove = [];
        if (
          loggedInUser &&
          loggedInUser !== res?.data?.loginOemDashboard?.user
        ) {
          for (let i = 0; i < localStorage.length; i++) {
            const key = localStorage.key(i);
            if (key && key.endsWith("_FILTERS")) {
              keysToRemove.push(key);
            }
          }
        }
        keysToRemove.forEach((key) => localStorage.removeItem(key));
      })
      .catch((e) => {
        if (!!callback && isFunction(callback)) callback(e);
      });
  };

  return {
    user: currentUser,
    chat,
    loading,
    login,
    isLoggedIn: !!currentUser && !!Object.keys(currentUser).length,
  };
};

export default useAuth;
